import React, { useMemo } from "react";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";

import { Button } from "@riot-private/ux-react-components";

import { CurrencyIconMap } from "@/utils/currencyIconMap";
import { CloseIcon } from "@/utils/icons";
import { AssetType, ProductGame, ProductType } from "@/@types/common";
import { useI18n } from "@/utils/rioti18n";
import {
  Actions,
  BodyContainer,
  ButtonContentWrapper,
  ButtonPriceWrapper,
  BuyActions,
  ImageContainer,
  ItemName,
  ItemType,
  PriceWrapper,
  ProductCost,
  ProductDetails,
  ProductLabel,
  ProductModalContainer,
  ProductNudge,
} from "./productModalStyles";
import { navigate } from "gatsby";
// import { useContentstackImage } from "@/providers/useContentstackImage";
import { CardProductType, ProductIdentity, ProductInBundle } from "@/@types/data/collections";
import useMediaQuery from "@/providers/useMediaQuery";
import { device } from "@/style/constants";
import { useContentstackImage } from "@/providers/useContentstackImage";
import { usePurchaseInfo } from "@/providers/usePurchaseInfo";
import { CURRENCY_BY_GAME } from "@/variables";
import { CurrencyIconProps } from "@/assets/svgs/CurrencyIcons";
import { PurchaseState } from "@/@types/purchase";

export type PropTypes = {
  product: ProductInBundle;
  closeModal: () => void;
};

/**
 * Simplifies looking for the product from the API response.
 * Handles casing and levels for products that have them
 *
 * @param needle
 * @param haystackProduct
 * @returns
 */
const matchIdOrLevelId = (needle: string | null, haystackProduct: ProductInBundle | null) => {
  if (needle === null || haystackProduct === null) {
    return false;
  }
  const loweredNeedle = needle.toLowerCase();
  if (haystackProduct.levels) {
    const levels = haystackProduct.levels.map((level) => level.id.toLowerCase());
    return levels.includes(loweredNeedle);
  }
  return haystackProduct.id.toLowerCase() === loweredNeedle;
};

const ButtonPriceContent = ({
  color,
  CurrencyIcon,
  price,
  label,
}: {
  color?: string;
  price?: number | null;
  label: string;
  CurrencyIcon: React.ElementType<CurrencyIconProps>;
}) => {
  return (
    <ButtonContentWrapper>
      <ButtonPriceWrapper>
        <CurrencyIcon size={19} fill={color} />
        <p>{price}</p>
      </ButtonPriceWrapper>
      <p>{label}</p>
    </ButtonContentWrapper>
  );
};

export const ProductModal = ({ closeModal, product }: PropTypes) => {
  const { t, navigate: i18nNavigate } = useI18n();

  // Subscribe to the width of page to properly set icon colors
  const isDesktop = useMediaQuery(device.laptop);

  const CurrencyIcon = CurrencyIconMap[product.gameId];

  const productImage =
    product.type === CardProductType.WEAPON_SKIN
      ? // if it is a skin, bring the default asset
        product.assets?.find((asset) => {
          return asset?.productId === product.defaultChromaId && asset.type === AssetType.FULL;
        })
      : // if it is not a skin bring the biggest asset
        product.assets?.reduce(
          (prev, curr) => ((curr?.width || 0) > (prev?.width || 0) ? curr : prev),
          product.assets[0],
        );

  const level = product.levels?.find((level) => level.name.indexOf(" Level") === -1);
  const productsToAsk: ProductIdentity[] = [
    // ITEM
    {
      gameId: product.gameId as ProductGame,
      productType: product.type as string,
      productId: level?.id.toLowerCase() ?? product.id.toLowerCase(), // TODO: This should be the level when available.
    },
  ];
  if (product.bundleId) {
    productsToAsk.push(
      // BUNDLE
      {
        gameId: product.gameId as ProductGame,
        productType: ProductType.BUNDLE,
        productId: product.bundleId.toLowerCase(),
      },
    );
  }
  const { products } = usePurchaseInfo(productsToAsk);

  const item = products.find((p) => matchIdOrLevelId(p.identity.productId, product));
  const bundle = products.find((p) => p.identity.productId === product.bundleId.toLowerCase());
  const productLevels = product.levels?.map((level) => level.id.toLowerCase()) ?? [product.id.toLowerCase()];
  const itemInBundle = bundle?.contents
    ?.find((content) => productLevels.includes(content.productIdentity.productId.toLowerCase()))
    ?.prices.find((price: any) => price.currency === CURRENCY_BY_GAME[ProductGame.Valorant]);

  const productOwned = item?.state === PurchaseState.ALREADY_OWNED;

  const productMessages = useMemo(() => {
    let nudgeMessage = "";
    let labelMessage = "";

    /* istanbul ignore next: else */
    if (item && itemInBundle) {
      const itemPriceInBundle = itemInBundle.cost;
      const cost = item.cost;
      if (itemPriceInBundle === 0) {
        nudgeMessage = t("dbuy.product.buy.nudge.free");
        /* istanbul ignore next: else */
      } else if (itemPriceInBundle && (cost || 0) && itemPriceInBundle < (cost || 0)) {
        const discount = Math.ceil((itemPriceInBundle / (cost || 0)) * 100);
        nudgeMessage = t("dbuy.product.buy.nudge.percentage_off", { discount });
      }
    }

    if (productOwned) {
      nudgeMessage = t("dbuy.product.buy.owned");
      labelMessage = t("dbuy.product.buy.owned.limit_single");
    }

    return { nudgeMessage, labelMessage };
  }, [product, item, bundle, itemInBundle, productOwned]);

  const asset = useContentstackImage({
    image: { url: productImage?.url },
    width: productImage?.width,
    height: productImage?.height,
    layout: "constrained",
  });

  return (
    <ProductModalContainer>
      {productMessages?.nudgeMessage && <ProductNudge>{productMessages?.nudgeMessage}</ProductNudge>}
      <Button.Secondary
        className="close"
        buttonSize="S"
        onClick={closeModal}
        label={(<CloseIcon />) as unknown as string}
      ></Button.Secondary>
      <ImageContainer>
        <div className="background-gradient" />
        <StaticImage
          className="background-image"
          loading="eager"
          src={"../../../../../assets/images/tmp-val-modal-item-background.png"}
          alt="background"
        />
        {productImage && (
          <GatsbyImage
            className="product-asset"
            objectFit="contain"
            image={asset}
            alt={t("dbuy.page.generic.image.alt.splash")}
          ></GatsbyImage>
        )}
      </ImageContainer>
      <BodyContainer>
        <ProductDetails>
          <ItemType>{t(`dbuy.product.type.${product.type?.toLowerCase()}`)}</ItemType>
          <ItemName>{product.name}</ItemName>
          <PriceWrapper>
            <CurrencyIcon
              size={28}
              fill={
                isDesktop
                  ? // gray.04-alpha.90
                    "#0a0a0ae6"
                  : // gray.99
                    "#FCFCFC"
              }
            />
            <ProductCost>{item?.cost}</ProductCost>
          </PriceWrapper>
          {productMessages?.labelMessage && <ProductLabel>{productMessages?.labelMessage}</ProductLabel>}
        </ProductDetails>
        <Actions className={`actions--${product.showBundleLink ? "large" : "small"}`}>
          <BuyActions>
            {product.bundleId && (
              <Button.Primary
                className="buy-bundle"
                buttonSize="M"
                onClick={() => navigate("/")}
                label={
                  (
                    <ButtonPriceContent
                      color={
                        isDesktop
                          ? // gray.99
                            "#FCFCFC"
                          : // gray.04-alpha.90
                            "#0a0a0ae6"
                      }
                      CurrencyIcon={CurrencyIcon}
                      price={bundle?.cost ?? 0}
                      label={t("dbuy.product.buy.buy_bundle")}
                    />
                  ) as any as string
                }
              />
            )}
            <Button.Primary
              gradient
              buttonSize="M"
              className={`buy-item ${productOwned ? "buy-item--disabled" : ""}`}
              disabled={productOwned}
              onClick={() => navigate("/")}
              label={
                productOwned
                  ? t("dbuy.product.buy.owned")
                  : ((
                      <ButtonPriceContent
                        // gray.04-alpha.90
                        color="#0a0a0ae6"
                        CurrencyIcon={CurrencyIcon}
                        price={item?.cost}
                        label={t("dbuy.product.buy.buy_item")}
                      />
                    ) as any as string)
              }
            />
          </BuyActions>
          {product.bundleId && product.showBundleLink && (
            <Button.Primary
              className="see-bundle"
              buttonSize="M"
              onClick={() => i18nNavigate(product.bundleUrl)}
              label={t("dbuy.product.buy.bundle_details")}
            />
          )}
        </Actions>
      </BodyContainer>
    </ProductModalContainer>
  );
};
